import { ResetAlt } from '@carbon/react/icons';
import { Button, Modal } from '@carbon/react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDataTableContext } from '../context';
import styles from './table-toolbar.module.scss';

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Reset button with confirmation modal for restoring table defaults.
 * Resets column visibility, order, pinning, sizing, and sorting.
 * @return {JSX.Element} The reset button and modal elements
 */
export function ResetCustomization() {
  // `resetAllCustomizations` is the full "reset to default" — it reverts layout, sort AND
  // filters and fires every callback with one consistent snapshot, so no separate
  // `resetAllFilters()` call is needed here (which used to fire a second, partial snapshot).
  const { resetAllCustomizations } = useDataTableContext();

  // Local state
  const [showResetModal, setShowResetModal] = useState(false);

  // Handlers
  const handleReset = () => {
    resetAllCustomizations();
    setShowResetModal(false);
  };

  return (
    <>
      {createPortal(
        <Modal
          open={showResetModal}
          modalHeading="Reset table to default"
          danger
          size="xs"
          primaryButtonText="Reset to default"
          secondaryButtonText="Cancel"
          className={styles.resetTableModal}
          onRequestClose={() => setShowResetModal(false)}
          onRequestSubmit={handleReset}
        >
          <p>
            Are you sure you want to reset the table to its default settings?
          </p>
          <p>This action will revert all changes and cannot be undone.</p>
        </Modal>,
        document.body
      )}
      <Button
        hasIconOnly
        renderIcon={ResetAlt}
        kind="ghost"
        size="lg"
        iconDescription="Reset table to default"
        tooltipPosition="bottom"
        // The toolbar's last control: centred, its tooltip runs past the table's right edge.
        tooltipAlignment="end"
        onClick={() => setShowResetModal(true)}
      />
    </>
  );
}
